import { useQueries } from '@tanstack/react-query';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useOwnerNames } from 'src/financial/financial-data/hooks/useOwnerNames';
import { FinancialItems } from 'src/financial/financial-data/hooks/useFinancialItems';
import {
  FinancialCategory,
  buildCategories,
  emptyGroupedRows,
  extractRows,
  mergeGroupedRows,
} from 'src/financial/financial-data/rework/financialRows';
import { FamilyMember } from './useFamilyMembers';

export function useFamilyFinancialItems(members: FamilyMember[]): {
  categories: FinancialCategory[];
  isLoading: boolean;
  hasErrors: boolean;
  /**
   * No member's items ever arrived, so the tab has no figures at all rather than partial ones.
   * A refetch that fails over data already loaded is not this: it leaves the rows standing and
   * is reported by `hasErrors`.
   */
  allFailed: boolean;
  refetch: () => Promise<unknown>;
} {
  const { flagEnabledFinancialItemOwners } = useFlags();
  const results = useQueries({
    queries: members.map(member => ({
      queryKey: ['clients', member.clientId, 'financial-items-bff'],
      enabled: !!member.clientId,
    })),
  });

  const isLoading = results.some(result => result.isLoading);
  const hasErrors = results.some(result => result.isError);
  const allFailed = results.length > 0 && results.every(result => result.isLoadingError);
  const refetch = () => Promise.all(results.map(result => result.refetch()));

  // The family view has no "me", so an item names every owner it records rather than showing one
  // member's share, including owners outside the family.
  const resolveOwnerName = useOwnerNames(
    members,
    members.map(member => member.clientId),
  );

  // Owners are only read with the feature on; without it the family view keeps whole values.
  const share = flagEnabledFinancialItemOwners
    ? { kind: 'household' as const, memberClientIds: members.map(member => member.clientId) }
    : undefined;

  const grouped = members.map((member, index) => {
    const data = results[index].data as FinancialItems | undefined;
    if (!data) return emptyGroupedRows();
    // Full name throughout: rows that record owners list full names, so the fallback has to
    // match or the column reads as a mix of "Alice Smith" and "Alice".
    const name = resolveOwnerName(member.clientId) ?? member.client.firstName;
    return extractRows(data, { clientId: member.clientId, name }, share, resolveOwnerName);
  });

  const categories = buildCategories(mergeGroupedRows(grouped));

  return { categories, isLoading, hasErrors, allFailed, refetch };
}
